import { Logger } from '@frontend/logger';
import { MqttSettingClient } from '@frontend/setting/api';
import type { MqttSetting } from '@frontend/setting/types';
import mqtt, { type IClientOptions, type MqttClient } from 'mqtt';
import { Subject } from 'rxjs';

export enum MqttConnectionStatus {
    CONNECTED = 'connected',
    DISCONNECTED = 'disconnected',
    ERROR = 'error',
    CONNECTING = 'connecting',
    UNKNOWN = 'unknown'
}

/**
 * Keepalive must stay below typical load-balancer idle timeouts (~60s).
 * mqtt.js sends the first PINGREQ after `keepalive` seconds, so 30s leaves
 * headroom before a 60s idle close — the failure mode seen in kiosk PCAPdroid captures.
 */
const MQTT_KEEPALIVE_SECONDS = 30;
const MQTT_CONNECT_TIMEOUT_MS = 30_000;
const MQTT_RECONNECT_PERIOD_MS = 1_000;
const MQTT_CLIENT_ID_STORAGE_KEY = 'telloport.mqtt.clientId';

export default class MqttConnection {
    private static instance: MqttConnection | null = null;

    private client: MqttClient | null = null;
    private _topics: string[] = [];
    private pendingSubscriptions: string[] = [];
    private pendingUnsubscriptions: string[] = [];
    private networkListenersAttached = false;

    private _status: MqttConnectionStatus = MqttConnectionStatus.UNKNOWN;
    private _statusSubject = new Subject<MqttConnectionStatus>();

    public get status$() {
        return this._statusSubject.asObservable();
    }

    public get status(): MqttConnectionStatus {
        return this._status;
    }

    public get topics(): string[] {
        return this._topics;
    }

    private _messageSubject = new Subject<{ topic: string; payload: { message: string; data: any } }>();

    public get message$() {
        return this._messageSubject.asObservable();
    }

    private constructor() {
        this.setStatus(MqttConnectionStatus.CONNECTING);
        MqttSettingClient.fetchMqttSettings()
            .then((settings) => {
                this.client = mqtt.connect(this.buildClientOptions(settings));
                this.init(this.client);
                this.attachNetworkListeners();
            })
            .catch((error) => {
                Logger.error('MQTT failed to load settings', error);
                this.setStatus(MqttConnectionStatus.ERROR);
            });
    }

    private setStatus(status: MqttConnectionStatus): void {
        this._status = status;
        this._statusSubject.next(status);
    }

    static getInstance(): MqttConnection {
        if (this.instance == null) {
            this.instance = new MqttConnection();
        }
        return this.instance;
    }

    private buildClientOptions(settings: MqttSetting): IClientOptions {
        const port = Number(process.env['NX_PUBLIC_MQTT_PORT']);
        const protocol = process.env['NX_PUBLIC_MQTT_PROTOCOL'] as mqtt.MqttProtocol | undefined;

        return {
            host: process.env['NX_PUBLIC_MQTT_HOSTNAME'] || settings.mqtt_hostname,
            port: Number.isFinite(port) ? port : undefined,
            protocol,
            path: process.env['NX_PUBLIC_MQTT_PATH'] || '/mqtt',
            username: settings.mqtt_username || undefined,
            password: settings.mqtt_password || undefined,
            clientId: this.getOrCreateClientId(),
            keepalive: MQTT_KEEPALIVE_SECONDS,
            connectTimeout: MQTT_CONNECT_TIMEOUT_MS,
            reconnectPeriod: MQTT_RECONNECT_PERIOD_MS,
            clean: true,
            resubscribe: true,
            reschedulePings: true,
            // Worker timers avoid main-thread timer throttling that drops PINGREQ in browsers/WebViews.
            timerVariant: 'worker',
            protocolVersion: 4
        };
    }

    private getOrCreateClientId(): string {
        const prefix = 'telloport-';
        try {
            const existing = globalThis.localStorage?.getItem(MQTT_CLIENT_ID_STORAGE_KEY);
            if (existing) {
                return existing;
            }
            const clientId = `${prefix}${Math.random().toString(16).slice(2, 10)}`;
            globalThis.localStorage?.setItem(MQTT_CLIENT_ID_STORAGE_KEY, clientId);
            return clientId;
        } catch {
            return `${prefix}${Math.random().toString(16).slice(2, 10)}`;
        }
    }

    onMessage(topic: string, message: Uint8Array): void {
        try {
            const payload = JSON.parse(new TextDecoder().decode(message));
            Logger.debug('MQTT message received', topic);
            this._messageSubject.next({ topic, payload });
        } catch (error) {
            Logger.error('MQTT message parse failed', { topic, error });
        }
    }

    onConnect(): void {
        Logger.log('Connected to MQTT broker', {
            clientId: this.client?.options.clientId,
            keepalive: MQTT_KEEPALIVE_SECONDS
        });
        this.setStatus(MqttConnectionStatus.CONNECTED);
        void this.processPendingSubscriptions();
    }

    onError(event: Error | unknown): void {
        Logger.error('MQTT connection error', event);
        this.setStatus(MqttConnectionStatus.ERROR);
    }

    onDisconnect(): void {
        Logger.log('Disconnected from MQTT broker');
        this.setStatus(MqttConnectionStatus.DISCONNECTED);
    }

    onClose(): void {
        if (this.client?.disconnecting) {
            return;
        }
        Logger.warn('MQTT connection closed', {
            clientId: this.client?.options.clientId,
            willReconnect: (this.client?.options.reconnectPeriod ?? 0) > 0
        });
        if (this._status !== MqttConnectionStatus.DISCONNECTED) {
            this.setStatus(MqttConnectionStatus.CONNECTING);
        }
    }

    onOffline(): void {
        Logger.warn('MQTT client offline');
        if (this._status !== MqttConnectionStatus.DISCONNECTED) {
            this.setStatus(MqttConnectionStatus.CONNECTING);
        }
    }

    onReconnect(): void {
        Logger.log('MQTT reconnecting');
        this.setStatus(MqttConnectionStatus.CONNECTING);
    }

    async disconnect(): Promise<void> {
        this.detachNetworkListeners();
        if (this.client) {
            this.client.end(true);
        }
    }

    private async processPendingSubscriptions(): Promise<void> {
        if (this.pendingSubscriptions.length > 0) {
            Logger.debug('Processing pending subscriptions', this.pendingSubscriptions);
            for (const topic of this.pendingSubscriptions) {
                await this.subscribe(topic);
            }
            this.pendingSubscriptions = [];
        }
        if (this.pendingUnsubscriptions.length > 0) {
            Logger.debug('Processing pending unsubscriptions', this.pendingUnsubscriptions);
            for (const topic of this.pendingUnsubscriptions) {
                await this.unsubscribe(topic);
            }
            this.pendingUnsubscriptions = [];
        }
    }

    async subscribe(topic: string): Promise<void> {
        if (!this.client || this._status !== MqttConnectionStatus.CONNECTED) {
            Logger.debug('MQTT not connected yet, queuing subscription', {}, topic);
            if (!this.pendingSubscriptions.includes(topic)) {
                this.pendingSubscriptions.push(topic);
            }
            return;
        }
        await this.client.subscribeAsync(topic).then(() => {
            if (this.topics.indexOf(topic) === -1) this.topics.push(topic);
        });
    }

    async unsubscribe(topic: string): Promise<void> {
        if (!this.client || this._status !== MqttConnectionStatus.CONNECTED) {
            Logger.debug('MQTT not connected yet, queuing unsubscription', {}, topic);
            if (!this.pendingUnsubscriptions.includes(topic)) {
                this.pendingUnsubscriptions.push(topic);
            }
            return;
        }
        await this.client.unsubscribeAsync(topic).then(() => {
            if (this.topics.indexOf(topic) !== -1) this.topics.splice(this.topics.indexOf(topic), 1);
        });
    }

    /**
     * Nudge recovery when the runtime resumes (tab visible again / network back).
     * mqtt.js auto-reconnects, but browsers often pause work while hidden.
     */
    private recoverConnectionIfNeeded(): void {
        if (!this.client || this._status === MqttConnectionStatus.DISCONNECTED) {
            return;
        }
        if (this.client.connected || this.client.reconnecting || this.client.disconnecting) {
            return;
        }
        Logger.log('MQTT recovery nudge — forcing reconnect');
        this.setStatus(MqttConnectionStatus.CONNECTING);
        this.client.reconnect();
    }

    private readonly onBrowserOnline = (): void => {
        Logger.log('Browser online — checking MQTT connection');
        this.recoverConnectionIfNeeded();
    };

    private readonly onVisibilityChange = (): void => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            this.recoverConnectionIfNeeded();
        }
    };

    private attachNetworkListeners(): void {
        if (this.networkListenersAttached || typeof window === 'undefined') {
            return;
        }
        window.addEventListener('online', this.onBrowserOnline);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.networkListenersAttached = true;
    }

    private detachNetworkListeners(): void {
        if (!this.networkListenersAttached || typeof window === 'undefined') {
            return;
        }
        window.removeEventListener('online', this.onBrowserOnline);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.networkListenersAttached = false;
    }

    private init(client: MqttClient): void {
        client.on('connect', this.onConnect.bind(this));
        client.on('message', this.onMessage.bind(this));
        client.on('error', this.onError.bind(this));
        client.on('disconnect', this.onDisconnect.bind(this));
        client.on('close', this.onClose.bind(this));
        client.on('offline', this.onOffline.bind(this));
        client.on('reconnect', this.onReconnect.bind(this));
        client.on('end', this.onDisconnect.bind(this));
    }
}
